/**
 * Frontend Entry Point
 * React application with TypeScript and Vite
 */

import * as Sentry from "@sentry/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import React from "react"
import ReactDOM from "react-dom/client"
import App from "./App"
import { ErrorBoundary } from "./components/ErrorBoundary"
// Validate env first so a misconfigured deploy fails loudly on boot rather
// than mid-render. Importing ./config/env throws on validation failure.
import { env } from "./config/env"
import { registerChunkReload } from "./lib/chunk-reload"
import { beforeSendFilter } from "./lib/sentry-filter"
import "./assets/styles/index.css" // Single source of truth for all styles

// Recover from stale lazy-chunk references after a deploy (issue #530).
registerChunkReload()

// Initialize Sentry only in staging/production (not development)
// Check environment: VITE_SENTRY_ENV takes priority, falls back to MODE
if (env.VITE_SENTRY_DSN) {
  const sentryEnv = env.VITE_SENTRY_ENV || env.MODE

  // Skip initialization in development to avoid polluting Sentry with local errors
  if (sentryEnv === "development") {
    console.log("Sentry DSN set but environment is development, skipping initialization")
  } else {
    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,
      environment: sentryEnv,
      sendDefaultPii: env.VITE_SENTRY_SEND_DEFAULT_PII,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      // Drop non-actionable browser/OS noise (e.g. iOS NotReadableError) to
      // avoid burning Sentry quota. See lib/sentry-filter.
      beforeSend: beforeSendFilter,
    })
  }
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Error fallback component for Sentry ErrorBoundary
interface ErrorFallbackProps {
  error: unknown
  resetError: () => void
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const errorMessage = error instanceof Error ? error.message : "An unknown error occurred"

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
        <p className="mt-2 text-muted-foreground">{errorMessage}</p>
        <button
          onClick={resetError}
          className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground"
        >
          Try again
        </button>
      </div>
    </div>
  )
}

const rootElement = document.getElementById("root")
if (!rootElement) {
  throw new Error("Failed to find the root element")
}
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
